import { DateTime } from 'luxon';
import React, { useContext, useMemo, useState } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import MenuItem from '@mui/material/MenuItem/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

import { customFrequencyUtils } from 'app-components/flux/scheduled-transactions';
import QChoiceChips from 'components/QuickenControls/QChoiceChips';
import { FrequencyContext } from './state';

import { makeStyles } from 'tss-react/mui';

const {
  SELECT_DAYS,
  ORDINAL_DAYS,
  ORDINAL_WEEKS,
  sortValuesContainingLast,
  DAY_SELECT_TYPE,
  indexToWeekday,
  weekdayToIndex,
} = customFrequencyUtils;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  freqDescription: {
    paddingTop: 16,
    paddingBottom: 8,
    fontSize: 12,
  },
  selectRoot: {
    marginTop: 8,
  },
  select: {
    padding: 8,
    paddingLeft: 16,
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
  },
  outlinedInput: {
    backgroundColor: theme.palette.background.default,
    minHeight: 48,
    '.MuiOutlinedInput-input': {
      padding: 8,
      '&:focus': {
        backgroundColor: theme.palette.background.default,
      },
    },
  },
  chip: {
    fontSize: 14,
    backgroundColor: theme.palette.background.layer1,
    borderRadius: 20,
    '.MuiChip-deleteIcon': {
      height: 20,
      width: 20,
      color: theme.palette.icon.light,
    },
  },
  errorText: {
    color: theme.palette.error.main,
  },
  buttonToggle: {
    backgroundColor: theme.palette.border.light,
    padding: 0,
    margin: '8px 0px',
    width: 'fit-content',
    borderRadius: '12px',
    '& .MuiChip-root': {
      borderRadius: '12px',
      border: 0,
      margin: '2px !important',
      paddingLeft: '12px',
      paddingRight: '12px',
      paddingTop: '8px',
      paddingBottom: '8px',
      backgroundColor: theme.palette.border.light,
      height: 'auto',
      fontSize: '14px',
    },
    '& .MuiChip-selected': {
      backgroundColor: theme.palette.background.paper,
      color: theme.palette.text.primary,
    },
  },
}));

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;

function Monthly() {
  const { dispatch, state, error } = useContext(FrequencyContext);
  const { classes } = useStyles();
  const theme = useTheme();

  const [isOpen, setIsOpen] = useState(false);

  const selectedType = useMemo(() => (state.byDay ? DAY_SELECT_TYPE.DOW : DAY_SELECT_TYPE.DOM), [state]);
  const dayOfWeek = useMemo(
    () => (state.byDay ? weekdayToIndex(state.byDay) : DateTime.local().minus({ day: 1 }).weekday),
    [state],
  );

  const occurenceOfDay = useMemo(() => {
    if (state.dueOn) {
      const dayOfMonth = state.dueOn.day;
      const dayOfWeek = state.dueOn.localWeekday;
      const firstDayOfMonth = state.dueOn.startOf('month');
      const firstDayOfWeek = firstDayOfMonth.localWeekday;
      const occurrence = Math.floor((dayOfMonth + (firstDayOfWeek - dayOfWeek) + 6) / 7);
      return occurrence;
    }
    return 1;
  }, [state.dueOn]);

  // @ts-expect-error FIXME TS7006
  const dispatchDayChanges = (newValues) => {
    if (selectedType === DAY_SELECT_TYPE.DOM) {
      dispatch({ type: 'UPDATE_MONTHLY_BY_DAYS_IN_MONTH', payload: newValues });
    } else {
      dispatch({ type: 'UPDATE_MONTHLY_BY_DAY_OF_WEEK', payload: { dayOfWeek: state.byDay, occurences: newValues } });
    }
  };

  // @ts-expect-error FIXME TS7006
  const handleSelectTypeChange = (item) => {
    if (item !== selectedType) {
      const date = state.dueOn || DateTime.local();
      if (item === DAY_SELECT_TYPE.DOW) {
        const dow = indexToWeekday(date.weekday - 1); // luxon week starts on Mon, so compensate
        // dispatch({ type: 'UPDATE_MONTHLY_BY_WEEKDAY', payload: dow });
        dispatch({ type: 'UPDATE_MONTHLY_BY_DAY_OF_WEEK', payload: { dayOfWeek: dow, occurences: [occurenceOfDay] } });
      } else if (item === DAY_SELECT_TYPE.DOM) {
        const dom = date.day;
        dispatch({ type: 'UPDATE_MONTHLY_BY_MONTHDAY', payload: [dom] });
      }
    }
  };

  // @ts-expect-error FIXME TS7006
  const handleDayOfWeekChange = (event) => {
    const selected = event.target.value;
    dispatch({ type: 'UPDATE_MONTHLY_BY_WEEKDAY', payload: indexToWeekday(selected) });
  };

  // @ts-expect-error FIXME TS7006
  const handleSelectChange = (event) => {
    setIsOpen(false); // close dropdown after each selection
    const newValues = event?.target?.value ? sortValuesContainingLast(event.target.value) : [];
    dispatchDayChanges(newValues);
  };

  // @ts-expect-error FIXME TS7006
  const handleDayDelete = (_e, item) => {
    const prop = selectedType === DAY_SELECT_TYPE.DOM ? state.byMonthDay : state.bySetPos;
    const tempValue = prop?.filter((existing) => existing !== item.value && !(item.value < 0 && existing > 28)) || [];
    const newValues = tempValue.length === 0 ? undefined : tempValue;
    dispatchDayChanges(newValues);
  };

  // @ts-expect-error FIXME TS7006
  const renderSelect = (all, selected, testId) => (
    <FormControl id='days-select' data-testid={testId} sx={{ width: '100%' }}>
      <Select
        sx={{ p: 0 }}
        id='days-select'
        variant='filled'
        data-testid={testId}
        multiple
        value={selected}
        open={isOpen}
        onClick={() => setIsOpen(!isOpen)}
        onChange={handleSelectChange}
        error={error}
        input={<OutlinedInput id='select-multiple-chip' className={classes.outlinedInput} />}
        renderValue={(values) => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 0 }}>
            {/*
             // @ts-expect-error FIXME TS7006 */}
            {values.map((value) => {
              // @ts-expect-error FIXME TS7006
              const item = all.find((day) => day.value === value) || { name: 'Last', value: -1 };
              return (
                <Chip
                  key={item.value}
                  label={item.name}
                  clickable
                  deleteIcon={<ClearIcon onMouseDown={(event) => event.stopPropagation()} />}
                  className={classes.chip}
                  onDelete={(e) => handleDayDelete(e, item)}
                />
              );
            })}
          </Box>
        )}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
              width: 250,
              paddingRight: 8,
            },
            sx: {
              '& .MuiList-root': {
                paddingBottom: 0,
              },
              '& .MuiMenuItem-root[data-value="-1"] ': {
                position: 'sticky',
                borderRadius: 0,
                borderTop: `1px solid ${theme.palette.border.light}`,
                backgroundColor: theme.palette.background.paper,
                bottom: 0,
                mt: 1,
                py: 1,
                px: 2,
                '& div': {
                  width: '100%',
                  borderRadius: 2,
                  px: 2,
                },
              },
              '& .MuiMenuItem-root[data-value="-1"]:hover': {
                borderRadius: 2,
                backgroundColor: theme.palette.action.hover,
                mt: 1,
                py: 1,
                '& div': {
                  backgroundColor: theme.palette.info.light,
                  mt: 0,
                  py: 1,
                },
              },
              '.Mui-selected': {
                borderRadius: '8px !important',
                backgroundColor: `${theme.palette.action.hover} !important`,
              },
            },
          },
        }}
      >
        {/*
         // @ts-expect-error FIXME TS7006 */}
        {all.map((day) => (
          <MenuItem key={day.name} value={day.value}>
            {day.name}
          </MenuItem>
        ))}
      </Select>
      {error && <FormHelperText className={classes.errorText}>Required</FormHelperText>}
    </FormControl>
  );

  return (
    <div id='monthly-recurrence' className={classes.root}>
      <Box className={classes.buttonToggle} data-testid='monthly-day-type'>
        <QChoiceChips
          items={Object.values(DAY_SELECT_TYPE)}
          selectedItems={[selectedType]}
          onSelected={(item) => handleSelectTypeChange(item[0])}
        />
      </Box>

      {selectedType === DAY_SELECT_TYPE.DOW && (
        <>
          <Typography className={classes.freqDescription}>Select a day of the week</Typography>
          <Select
            id='days-dropdown'
            variant='filled'
            classes={{ root: classes.selectRoot, select: classes.select }}
            value={dayOfWeek}
            data-testid='monthly-day-of-week-select'
            onChange={handleDayOfWeekChange}
          >
            {SELECT_DAYS.map((item) => (
              <MenuItem key={item.label} value={typeof item.value === 'number' ? item.value : item.value?.weekday}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </>
      )}

      {selectedType === DAY_SELECT_TYPE.DOM && (
        <>
          <Typography className={classes.freqDescription}>Select one or more days in the month</Typography>
          {renderSelect(ORDINAL_DAYS, state.byMonthDay || [], 'monthly-day-of-month-select')}
        </>
      )}

      {selectedType === DAY_SELECT_TYPE.DOW && state.byDay && (
        <>
          <Typography className={classes.freqDescription}>Select one or more occurences</Typography>
          {renderSelect(ORDINAL_WEEKS, state.bySetPos || [], 'monthly-occurrences-select')}
        </>
      )}
    </div>
  );
}

export default Monthly;
